import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Avatar,
  Link,
  Stack,
  InputLabel,
  IconButton,
  InputAdornment,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  AccountCircle, 
  Security,
  CorporateFare 
} from "@mui/icons-material";
import AuthContext from "./auth-context";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import bankingimage from "../images/banking.png";
import CryptoJS from "crypto-js";
import { motion } from "framer-motion";

export default function Login() {
  const authCtx = useContext(AuthContext);
  const initialValues = { UserName: "", Password: "", CoopID: "" };
  const [formValues, setFormValues] = useState(initialValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const url = process.env.REACT_APP_URL;
  const apiSignature = process.env.REACT_APP_SIGNATURE;
  
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
    if (formErrors.errorv) {
      setFormErrors({ ...formErrors, errorv: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate(formValues);
    setFormErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);
      
      try {
        const dataForm = {
          CoOperativeCode: formValues.CoopID,
          UserName: formValues.UserName,
          PassCode: formValues.Password,
          IsEncryptReq: "Y",
          TimeStamp: new Date().toISOString(),
        };

        const key = CryptoJS.enc.Utf8.parse("D89035A6634F4C4786B947518F17A18A");
        const iv = CryptoJS.enc.Utf8.parse("EasyS0ftS0ftware");

        const encrypted = CryptoJS.AES.encrypt(JSON.stringify(dataForm), key, {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });

        const dataToSend = { EncrytedText: encrypted.toString() };
        const response = await fetch(`${url}/Login/UserLogin`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Signature": apiSignature,
          },
          body: JSON.stringify(dataToSend),
        });

        const res = await response.json();
        
        if (res.statuS_CODE === "0") {
          const userInfo = {
            UserID: res.userID,
            UserName: res.userName,
            UserType: res.userType,
            categoryID: res.categoryID,
            customerAddress: res.customerAddress,
            customerName: res.customerName,
            coOperativeName: res.coOperativeName,
            coOperativeAddress: res.coOperativeAddress,
            coOperativeCode: res.coOperativeCode,
            coOperativeImg: res.coOperativeImg,
          };
          
          localStorage.setItem("userInfo", JSON.stringify(userInfo));
          sessionStorage.setItem("userInfo", JSON.stringify(userInfo));
          authCtx.login(res.userID);
          navigate(from, { replace: true });
        } else {
          setAlertMessage(res.message || "Login failed");
          setShowAlert(true);
        }
      } catch (error) {
        setAlertMessage("Network error. Please try again.");
        setShowAlert(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.UserName) errors.UserName = "Username is required";
    if (!values.CoopID) errors.CoopID = "Cooperative ID is required";
    if (!values.Password) errors.Password = "Password is required";
    return errors;
  };

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showAlert]);

  if (authCtx.isLoggedIn) {
    return <Navigate to={from} replace />;
  }

  return (
    <Grid container component="main" sx={{ height: "100vh", overflow: "hidden" }}>
      {/* Left: Form Section */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.default",
          p: isMobile ? 2 : 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 450,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Avatar
            sx={{
              m: 1,
              bgcolor: "primary.main",
              width: 70,
              height: 70,
              fontSize: "1.5rem",
              fontWeight: "bold",
            }}
          >
            esPay
          </Avatar>
          
          <Typography component="h1" variant="h4" fontWeight="600" gutterBottom>
            Welcome Back
          </Typography>
          
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
            Sign in to your esPay account to continue
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            {showAlert && (
              <Fade in={showAlert}>
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowAlert(false)}>
                  {alertMessage}
                </Alert>
              </Fade>
            )}

            <Stack spacing={3}>
              <Box>
                <InputLabel sx={{ mb: 1, fontWeight: 700 }}>
                  Cooperative Code
                </InputLabel>
                <TextField
                  fullWidth
                  placeholder="Enter your cooperative code"
                  name="CoopID"
                  value={formValues.CoopID}
                  onChange={handleChange}
                  error={!!formErrors.CoopID}
                  helperText={formErrors.CoopID}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CorporateFare color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box>
                <InputLabel sx={{ mb: 1, fontWeight: 700 }}>
                  Username
                </InputLabel>
                <TextField
                  fullWidth
                  placeholder="Enter your username"
                  name="UserName"
                  value={formValues.UserName}
                  onChange={handleChange}
                  error={!!formErrors.UserName}
                  helperText={formErrors.UserName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircle color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box>
                <InputLabel sx={{ mb: 1, fontWeight: 700 }}>
                  Password
                </InputLabel>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  name="Password"
                  value={formValues.Password}
                  onChange={handleChange}
                  error={!!formErrors.Password}
                  helperText={formErrors.Password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Security color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "1rem",
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </Stack>
          </Box>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
            © {new Date().getFullYear()} Made by{" "}
            <Link
              href="https://easysoftware.com.np/"
              target="_blank"
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              <Box component="span" color="error.main">Easy</Box>
              <Box component="span" color="primary.main">Software</Box>
            </Link>
          </Typography>
        </Box>
      </Grid>

      {/* Right: Banner Section */}
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "primary.main",
          color: "white",
          p: 4,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: "50%",
            backgroundColor: "primary.light",
            opacity: 0.2,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            backgroundColor: "primary.light",
            opacity: 0.2,
          }}
        />
        
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            maxWidth: 600,
          }}
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
        >
          <Typography variant="h3" fontWeight="600" gutterBottom>
            Banking Made Easy
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Experience the next generation of cooperative banking software
          </Typography>
          
          <Box
            component="img"
            src={bankingimage}
            alt="Banking Illustration"
            sx={{
              width: "100%",
              maxWidth: 400,
              filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.1))",
            }}
          />
          
          <Typography variant="body2" sx={{ mt: 4, opacity: 0.8 }}>
            Secure • Reliable • Efficient
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}